import axios from "axios";

/**
 * Business Analytics: figures added up across all Businesses, for internal staff.
 * Both endpoints require the `businessanalytics.view` permission.
 */

/**
 * One row per Reporting Month (calendar month, Europe/London): the last 12 full
 * months plus the current, partial month.
 * @returns {Promise<{ months: Array<Object>, currencies: string[], approvedBeforeTracking: number }>}
 */
export async function fetchBusinessAnalyticsMonthly() {
  const res = await axios.get("/analytics/business/monthly");
  return res.data.details;
}

/**
 * Wallet Position: total balance held right now across all Business wallets, per currency.
 * @returns {Promise<{ fetchedAt: string, currencies: Array<Object>, businessesChecked: number, businessesFailed: number }>}
 */
export async function fetchBusinessWalletPosition() {
  // A cold refresh reads every Business's wallets from the wallet service, one call each.
  // Give up after a minute rather than leave the page loading forever.
  const res = await axios.get("/analytics/business/wallet-position", { timeout: 60000 });
  return res.data.details;
}

/**
 * Business Profiles: one row per Company over the last 12 full Reporting Months of completed
 * transactions, with the cut-offs in force (`directionCutoff`, `reachLimits`, `sizeMinCompanies`).
 * Returns every Company; the page filters them itself with the same rules as the export.
 * @returns {Promise<{ months: string[], startsAt: string, endsAt: string, directionCutoff: number, reachLimits: { lowMax: number, mediumMax: number }, sizeMinCompanies: number, companies: Array<Object> }>}
 */
export async function fetchBusinessProfiles() {
  const res = await axios.get("/analytics/business/profiles");
  return res.data.details;
}

/**
 * Today's mid-market rates into GBP (`rates`: GBP per 1 unit of each currency code), for the
 * Collector → Disburser chart only. Its own request, so the table never waits on it.
 * @returns {Promise<{ currency: string, asOf: string, rates: Object<string, number> }>}
 */
export async function fetchBusinessProfilesFxRates() {
  const res = await axios.get("/analytics/business/profiles/fx-rates", { timeout: 20000 });
  return res.data.details;
}

/**
 * The Business Profiles table as CSV, with the given filters (see `profileFilterParams`).
 * @returns {Promise<{ blob: Blob, filename: string|null }>} filename from the server, if it sent one
 */
export async function exportBusinessProfiles(params) {
  const res = await axios.get("/analytics/business/profiles/export", { params, responseType: "blob" });
  const disposition = (res.headers && res.headers["content-disposition"]) || "";
  const match = disposition.match(/filename="?([^";]+)"?/);
  return { blob: res.data, filename: match ? match[1] : null };
}

/**
 * Sales Pipeline: count and value per Funnel Stage for each Reporting Month (the last 12 full
 * months plus the current one), from the uploaded HubSpot Deals export, with the last upload time.
 * @returns {Promise<{ pipeline: string, stages: string[], lostReasonCategories: string[], months: Array<Object>, lastUploadAt: string|null, valueFillRate: number|null, valueFields: string[], unmappedStages: Array<Object>, ignoredPipelines: string[] }>}
 */
export async function fetchSalesPipeline() {
  const res = await axios.get("/analytics/business/sales-pipeline", { params: { pipeline: "sales" } });
  return res.data.details;
}

/**
 * Uploads a HubSpot Deals export (.xlsx or .csv) as HubSpot produced it.
 * Requires `businessanalytics.upload` as well as `businessanalytics.view`.
 * @param {File} file
 * @returns {Promise<{ uploadId: number, fileName: string, rowCount: number, inserted: number, updated: number, rejected: number, uncategorisedLostReasons: number, warnings: string[], rejectedRows: Array<{ row: number, recordId: string|null, reason: string }> }>}
 */
export async function uploadHubSpotExport(file) {
  const form = new FormData();
  form.append("file", file);
  // A large export is parsed and upserted row by row, and new lost reasons are read by AI; allow it a
  // couple of minutes.
  const res = await axios.post("/analytics/business/sales-pipeline/uploads/deals", form, { timeout: 120000 });
  return res.data.details;
}
